import * as React from 'react';
import { Layout, Row, Col, Card } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	projectTemplatesRestCrudThunksForBuyer,
	projectTemplatesRestCrudThunksForSupplier,
} from '../../thunks/project_template_thunks';
import { TwoLineList } from '../two_line_list/TwoLineList';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getCurrency, getEntityById, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { mappingTemplateTypesForDisplay } from '../../utils/DataMappingUtils';
import { getFieldChoices, isMultiSelectField } from '../../utils/ProjectTemplateUtils';
import {
	TemplateDeliverableHeading,
	TemplateTaskLine,
	getDependsOnNumber,
} from '../project_template_form/ProjectTemplateItemDisplay';
import { isProjectsV2Enabled } from '../../utils/FeatureFlagUtils';

const { Content } = Layout;

const style = require('./ProjectTemplatesDetailDetailsPage.less');

// A field definition's type line. A multiple-choice field says how many answers may be
// picked and lists the options — the type alone ("multipleChoice") tells the reader
// nothing about what the field will actually ask.
const describeFieldType = (field) => {
	const choices = getFieldChoices(field);
	if (choices.length === 0) {
		return mappingTemplateTypesForDisplay(field);
	}
	return `${
		isMultiSelectField(field) ? 'Multiple choice (pick any)' : 'Multiple choice (pick one)'
	} — ${choices.join(', ')}`;
};

// Task rows read like the required/optional field lists above them: a hairline between
// every row, so a long list scans as rows rather than a block of text.
const TASK_ROW_CLASS = 'border-0 border-t border-solid border-grey-4 py-s pl-l hover:bg-grey-2';

const TemplateTaskRows = ({ tasks }) => (
	<div className="border-0 border-b border-solid border-grey-4">
		{tasks.map((task, index) => (
			<TemplateTaskLine key={index} task={task} className={TASK_ROW_CLASS} />
		))}
	</div>
);

interface ProjectTemplatesDetailPageProps {
	match: any;
}

class ProjectTemplatesDetailPage extends React.Component<any, any> {
	constructor(props) {
		super(props);
	}

	componentDidMount() {
		const { getProjectTemplate, match, currentUser } = this.props;
		const projectTemplateId = match.params.id;
		const userType = nullSafeGetOrElse('userType', currentUser, 'buyer');
		getProjectTemplate(userType, projectTemplateId);
	}

	render() {
		const { projectTemplate, projectTemplatesFetching, match, currentUser } = this.props;
		const projectTemplateId = match.params.id;
		const currency = getCurrency({ currentUser });
		const deliverables = nullSafeGetOrElse('deliverableTemplates', projectTemplate, []);
		const tasks = nullSafeGetOrElse('taskTemplates', projectTemplate, []);
		return projectTemplatesFetching ? (
			<PageLoadingPlaceholder />
		) : (
			<Content className="projectTemplatesDetailDetailsPage">
				{/*<ScrollToTopOnMount/>*/}
				{/*<BackTop/>*/}
				<Row style={{ margin: '-0.5em -8px 0.5em -8px' }} gutter={16}>
					<Col span={24}>
						<Card>
							<div className="projectTemplatesDetailDetailsPage__listSection">
								<div className="projectTemplatesDetailDetailsPage__listSectionTitle">
									Required Fields
								</div>
								{projectTemplate.requiredFields && projectTemplate.requiredFields.length === 0 ? (
									'No required fields'
								) : (
									<TwoLineList
										listItems={projectTemplate.requiredFields}
										lineOneRender={(record) => record.key}
										lineTwoRender={(record) => describeFieldType(record)}
									/>
								)}
							</div>
							<div className="projectTemplatesDetailDetailsPage__listSection">
								<div className="projectTemplatesDetailDetailsPage__listSectionTitle">
									Optional Fields
								</div>
								{projectTemplate.optionalFields && projectTemplate.optionalFields.length === 0 ? (
									'No optional fields'
								) : (
									<TwoLineList
										listItems={projectTemplate.optionalFields}
										lineOneRender={(record) => record.key}
										lineTwoRender={(record) => describeFieldType(record)}
									/>
								)}
							</div>
							{isProjectsV2Enabled() ? (
								<>
									<div className="projectTemplatesDetailDetailsPage__listSection">
										<div className="projectTemplatesDetailDetailsPage__listSectionTitle">
											Deliverables
										</div>
										{deliverables.length === 0
											? 'No deliverables'
											: deliverables.map((deliverable, index) => {
													const dependsOnNumber = getDependsOnNumber(deliverable, deliverables);
													const deliverableTasks = nullSafeGetOrElse('tasks', deliverable, []);
													return (
														<div key={index} className="mb-l">
															<TemplateDeliverableHeading
																number={index + 1}
																deliverable={deliverable}
																dependsOnNumber={dependsOnNumber}
																dependsOnName={
																	dependsOnNumber
																		? nullSafeGetOrElse(
																				'name',
																				deliverables[dependsOnNumber - 1],
																				'Untitled deliverable'
																		  )
																		: null
																}
																currency={currency}
															/>
															{deliverableTasks.length > 0 ? (
																<div className="mt-s">
																	<TemplateTaskRows tasks={deliverableTasks} />
																</div>
															) : null}
														</div>
													);
											  })}
									</div>
									<div className="projectTemplatesDetailDetailsPage__listSection">
										<div className="projectTemplatesDetailDetailsPage__listSectionTitle">Tasks</div>
										{tasks.length === 0 ? 'No tasks' : <TemplateTaskRows tasks={tasks} />}
									</div>
								</>
							) : null}
						</Card>
					</Col>
				</Row>
			</Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	projectTemplatesFetching: state.project_templates.fetching,
	// Keyed to the id in the route — `detail` holds whichever template was fetched
	// last, so it can bleed a stale record in from a previous route.
	projectTemplate: getEntityById(ownProps.match.params.id, state.project_templates),
	currentUser: state.session.currentUser,
	match: ownProps.match,
	history: ownProps.history,
});

const mapDispatchToProps = (dispatch) => ({
	getProjectTemplate: (userType, id) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectTemplatesRestCrudThunksForSupplier.readOne(id)
				: projectTemplatesRestCrudThunksForBuyer.readOne(id)
		),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(ProjectTemplatesDetailPage)
);
